import React, {
  CSSProperties,
  ElementType,
  ImgHTMLAttributes,
  startTransition,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { ImageData } from "./interface/ImageData";
import { ImageDataSources, Picture, SourceProps } from "./Picture/Picture";
import { _ImageRoot } from "./Image.styled";
import useOnScreen from "../../Hooks/useOnScreen";
import { preloadImage } from "./Image.utils";
import useImageLogic from "./Image.hooks";
import { CSSObject } from "styled-components";
import clsx from "clsx";

export interface IImageProps
  extends Omit<
    ImgHTMLAttributes<HTMLImageElement>,
    "placeholder" | "onLoad" | "src" | "srcSet" | "width" | "height"
  > {
  alt?: string;
  as?: ElementType;
  href?: string;
  className?: string;
  imgClassName?: string;
  image: ImageData;
  imgStyle?: CSSProperties;
  wrapperStyle?: CSSProperties;
  linkBreakpointToWrapper?: boolean;
  preload?: boolean;
  additionalImage?: Array<
    NonNullable<Pick<IImageProps, "image" | "wrapperStyle" | "imgStyle">>
  >;
  log?: boolean;
  /** LCP / hero — typings Img parfois sans `fetchPriority` selon @types/react */
  fetchPriority?: "high" | "low" | "auto";
  /**
   * LCP-FIX: force le chargement immédiat et prioritaire de l'image.
   * - applique `loading="eager"` (même si loading="lazy" demandé)
   * - applique `fetchpriority="high"`
   * - bypass l'IntersectionObserver (shouldLoad=true dès le 1er render)
   * À utiliser uniquement pour l'image LCP de la page (une seule par page).
   */
  priority?: boolean;
  /**
   * IntersectionObserver `rootMargin` pour le lazy gate (loading="lazy").
   * Agrandit le viewport virtuel pour démarrer le fetch avant que l’image
   * soit visible — ex. `"400px 0px"` ou `"50% 0px"`.
   * @default IMAGE_LAZY_ROOT_MARGIN
   */
  lazyRootMargin?: string;
  /**
   * Si true (défaut) et loading=lazy : src retardé via data-src jusqu’à l’IO (JS requis).
   * Si false : src/srcSet toujours dans le HTML + lazy natif navigateur
   * (charge dès le 1er paint si déjà dans / près du viewport, sans attendre le JS).
   */
  deferSrcUntilVisible?: boolean;
  // backgroundColor?: string;
  // objectFit?: CSSProperties["objectFit"];
  // objectPosition?: CSSProperties["objectPosition"];
  // onLoad?: (props: { wasCached: boolean }) => void;
  // onError?: ReactEventHandler<HTMLImageElement>;
  // onStartLoad?: (props: { wasCached: boolean }) => void;
}

// const defaultImageFormats = [ImageFormat.WEBP, ImageFormat.AUTO];
// const AUTO = "auto";

/**
 * Marge verticale IO par défaut (~½ viewport mobile) : lance le fetch un peu
 * avant l’entrée dans le viewport, sans eager global.
 */
export const IMAGE_LAZY_ROOT_MARGIN = "400px 0px";

const IMAGE_LAZY_IO_PARAMS = { ignoreReverse: true as const };

// main component
const Image = (props: IImageProps) => {
  const {
    image: imageIn,
    alt = "",
    as,
    href,
    wrapperStyle = {},
    className,
    imgClassName,
    imgStyle,
    loading = "lazy",
    preload,
    additionalImage,
    log,
    fetchPriority,
    priority,
    lazyRootMargin = IMAGE_LAZY_ROOT_MARGIN,
    deferSrcUntilVisible = true,
  } = props;

  const effectiveLoading = priority ? "eager" : loading;
  const effectiveFetchPriority = priority ? "high" : fetchPriority;
  const { layout = "constrained" } = imageIn;

  const { generateImageAspect } = useImageLogic();

  const additionalSource: Array<
    SourceProps & Pick<IImageProps, "wrapperStyle" | "imgStyle">
  > = [];
  const additionalImageMapped = additionalImage?.map((img) => {
    const { sources, wrapperStyle, imgStyle } = generateImageAspect(img.image);
    additionalSource.push(
      ...(sources.map((s) => ({ ...s, wrapperStyle, imgStyle })) || [])
    );

    return {
      ...img,
      wrapperStyle: { ...wrapperStyle, ...img.wrapperStyle },
      imgStyle: { ...imgStyle, ...img.imgStyle },
    };
  });

  // LCP-FIX: image « priority » — pas d’état loading initial (overlay ::before + transition).
  const [loaded, setLoaded] = React.useState(!!priority);
  // Lazy gate: ne pose src/srcSet que près du viewport (évite de fetch toute la page).
  // rootMargin = prefetch avant entrée viewport ; ignoreReverse = pas d’unload au scroll out.
  const lazyIoOptions = useMemo<IntersectionObserverInit>(
    () => ({
      root: null,
      rootMargin: lazyRootMargin,
      threshold: 0,
    }),
    [lazyRootMargin]
  );
  const [rootRef, isOnScreen] = useOnScreen(
    lazyIoOptions,
    IMAGE_LAZY_IO_PARAMS
  );

  const {
    sources,
    fallback,
    wrapperStyle: mainWrapperStyle,
    imgStyle: mainImgStyle,
    // Ne pas forcer aspectRadio globalement : ça casse les layouts qui
    // gèrent déjà le ratio via wrapperStyle / CSS parent (HighLightCard, etc.).
    // Le CLS below-fold est traité côté fallbacks SSR (getWidgetSsrFallback).
    // autoAspectRatio,
  } = generateImageAspect(imageIn);

  // compile source
  const imageDatas: ImageDataSources = { sources, fallback };

  // styled-components ajoute `px` aux numbers → aspectRatio: 0.666 devient `0.666px` (invalide).
  const normalizeWrapperStyle = (
    style: CSSProperties | undefined
  ): CSSObject => {
    if (!style) return {};
    const next: CSSObject = { ...style };
    if (typeof next.aspectRatio === "number") {
      next.aspectRatio = String(next.aspectRatio);
    }
    return next;
  };

  let responsiveStyle: CSSObject = {};
  const $sources = [
    {
      image: imageIn,
      wrapperStyle: { ...mainWrapperStyle, ...wrapperStyle },
      imgStyle: { ...mainImgStyle, ...imgStyle },
    },
    ...(additionalImageMapped || []),
  ];
  $sources?.forEach((item) => {
    const normalizedWrapper = normalizeWrapperStyle(item.wrapperStyle);
    const { aspectRatio, ...restImgStyle } = item.imgStyle || {};
    const imgCss: CSSObject = {
      ...restImgStyle,
      ...(aspectRatio != null ? { "aspect-ratio": `${aspectRatio}` } : {}),
    };

    if (item.image.media) {
      responsiveStyle[`@media ${item.image.media}`] = {
        ...normalizedWrapper,
        img: imgCss,
      };
    } else {
      responsiveStyle = {
        ...responsiveStyle,
        ...normalizedWrapper,
        img: imgCss,
      };
    }
  });

  useEffect(() => {
    if (preload) {
      preloadImage(imageDatas.fallback?.src || "");
    }
  }, [preload]);

  // HYDRATION-FIX: image déjà peinte dans le HTML statique — pas de flash opacity.
  useLayoutEffect(() => {
    if (priority || loaded) return;
    const img = rootRef.current?.querySelector("img");
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [priority, loaded, rootRef]);

  const onLoadPicture = (_e: React.SyntheticEvent<HTMLImageElement>) => {
    // LCP-FIX: pas de startTransition pour le hero — sinon report du « loaded » après d’autres updates React.
    if (priority) {
      setLoaded(true);
    } else {
      startTransition(() => {
        setLoaded(true);
      });
    }
  };

  // const isFullWidth = layout === "fullWidth";
  // const isConstrained = layout === "constrained";
  // const isFixed = layout === "fixed";

  // const placeholder =
  //   (autoAspectRatio !== AUTO && (
  //     <div
  //       aria-hidden={true}
  //       className={clsx("ro-image__placeholder")}
  //       style={{
  //         paddingTop: `${100 / autoAspectRatio}%`,
  //         opacity: loaded ? 0 : 1,
  //       }}
  //     ></div>
  //   )) ||
  //   null;

  const pictureSources = [
    ...(imageDatas.sources || []),
    ...(additionalSource || []),
  ];

  // deferSrcUntilVisible=false → src dans le HTML (lazy natif, pas de data-src).
  const shouldLoad = priority
    ? true
    : deferSrcUntilVisible && effectiveLoading === "lazy"
    ? loaded || isOnScreen
    : true;

  const hasResponsiveMedia = $sources.some((item) => !!item.image.media);
  const pictureStyle: CSSProperties = {
    ...(hasResponsiveMedia ? {} : imgStyle),
    opacity: loaded ? 1 : undefined,
  };

  log &&
    console.log("image", {
      pictureSources,
      sources,
      fallback,
    });

  return (
    <_ImageRoot
      ref={rootRef}
      {...{ layout, href, className: clsx(className, { loading: !loaded }) }}
      as={as as any}
      $css={responsiveStyle}
      // style={{
      //   width: isFullWidth ? "100%" : isFixed ? autoWidth : undefined,
      //   maxWidth: isConstrained ? autoWidth : undefined,
      //   maxHeight: isConstrained ? autoHeight : undefined,
      //   // ...wrapperStyle,
      // }}
    >
      {/* {!loaded && placeholder} */}
      <Picture
        onLoad={onLoadPicture}
        sources={pictureSources}
        fallback={imageDatas.fallback}
        className={imgClassName}
        style={pictureStyle}
        loading={effectiveLoading}
        alt={alt}
        log={log}
        // @ts-ignore
        fetchPriority={fetchPriority}
        // aspectRadio volontairement non passé — voir commentaire autoAspectRatio.
        shouldLoad={shouldLoad}
      />
    </_ImageRoot>
  );
};

export default Image;
